/**
 * The user's AI jurisdiction preference - one read, one write, both keys.
 *
 * Two keys carry the same value and must never drift:
 *   • `jurisdiction`            - legacy, still read by older backend paths
 *   • `CountryOfJurisdiction`   - authoritative, what the research resolver reads
 *
 * READ  → `CountryOfJurisdiction || jurisdiction`, via `readPreferredJurisdiction`.
 * WRITE → BOTH keys, via `withJurisdictionKeys`.
 *
 * `US` is not a jurisdiction this platform serves - it only ever existed as a
 * signup seed and a frontend hardcode. A persisted `US` is treated as UNSET:
 * nothing is preselected and the real default chain gets to answer.
 */

/** Values that mean "the user never actually chose a jurisdiction". */
const UNSET_JURISDICTION_VALUES = new Set(['us']);

export interface AIJurisdictionPreference {
  jurisdiction?: string | null;
  CountryOfJurisdiction?: string | null;
  [key: string]: any;
}

/** `''` when unset - never `'US'`. */
export const readPreferredJurisdiction = (
  aiSettings?: AIJurisdictionPreference | null
): string => {
  const raw = aiSettings?.CountryOfJurisdiction || aiSettings?.jurisdiction || '';
  const value = String(raw).trim();
  if (!value || UNSET_JURISDICTION_VALUES.has(value.toLowerCase())) return '';
  return value;
};

/**
 * Dual-write both jurisdiction keys from one chosen value.
 * An unset value writes `''` to both - never `'US'`, and never one key only.
 */
export const withJurisdictionKeys = <T extends Record<string, any>>(
  settings: T
): T & { jurisdiction: string; CountryOfJurisdiction: string } => {
  const value = readPreferredJurisdiction(settings as AIJurisdictionPreference);
  return { ...settings, jurisdiction: value, CountryOfJurisdiction: value };
};

/* ------------------------------------------------------------------ */
/*  Output language                                                    */
/* ------------------------------------------------------------------ */

/**
 * The AI OUTPUT language (`ai_settings.language`) - a language CODE, the same
 * vocabulary `/dropdown/ai-languages` serves and research compares
 * `output_language` against. This is NOT the UI language
 * (`general_settings.language`), which is a separate preference.
 *
 * One key, so "dual-write" here means: read and write it through this pair so
 * no caller re-invents a `|| 'en'` fallback. There is no platform language
 * default - unset is a valid state and means "answer neutrally".
 */
export interface AILanguagePreference {
  language?: string | null;
  [key: string]: any;
}

/** `''` when unset - never an invented language. */
export const readPreferredLanguage = (
  aiSettings?: AILanguagePreference | null
): string => String(aiSettings?.language || '').trim();

/**
 * Normalize the language key on its way out to a writer, so an unset language
 * is written as `''` rather than dropped or coerced to a country's tongue.
 */
export const withLanguage = <T extends Record<string, any>>(
  settings: T
): T & { language: string } => ({
  ...settings,
  language: readPreferredLanguage(settings as AILanguagePreference),
});
